import { configureStore } from "@reduxjs/toolkit";
import authReducer from "./auth/authSlice";
import accountsReducer from "./accounts/accountsSlice";
import executionLogReducer from "./execution-log/executionLogSlice";
import accountSetupReducer from "./account-setup/accountSetupSlice";
import filtersReducer from "./filters/filtersSlice";
import { rtkQueryErrorLoggerMiddleware } from "./middlewares/rtkQueryErrorLoggerMiddleware";
import { unauthenticatedMiddleware } from "./middlewares/unauthenticatedMiddleware";
import schedulesReducer from "./schedules/schedulesSlice";
import commitmentsSlice from "./commitments/commitmentsSlice";
import rolesSlice from "./user/userSlice";
import liveUsageMgmtSlice from "./live-usage-mgmt/liveUsageMgmtSlice";
import kubernetesLiveUsageMgmtSlice from "./kubernetes-live-usage-mgmt/kubernetesLiveUsageMgmtSlice";
import resourceExplorerSlice from "./resource-explorer/resourceExplorerSlice";
import cleanV2Reducer from "./clean-v2/cleanV2Slice";
import integrationsReducer from "./integrations/integrationsSlice";
import commonReducer from "./common/commonSlice";
import budgetsSlice from "./budgets/budgetsSlice";
import dashboardsSlice from "./dashboards/dashboardsSlice";
import dimensionsSlice from "./dimensions/dimensionsSlice";
import recommendationsSlice from "./recommendations/recommendationsSlice";
import navigationSlice from "./navigation/navigationSlice";
import automationsSlice from "./automations/automationsSlice";
import alertsReducer from "./alerts/alertsSlice";
import taskMgmtReducer from "./task-management/taskManagementSlice";
import reportsReducer from "./reports/reportsSlice";
import savingsOpportunitiesSlice from "./savings-opportunities/savingsOpportunitiesSlice";
import askAiSlice from "./ask-ai/askAiSlice";
import resourceDetailsSlice from "./resource-details/resourceDetailsSlice";
import resourcesGridSlice from "./resources-grid/resourcesGridSlice";
import explainWithAiSlice from "./explain-with-ai/explainWithAiSlice";
import providerConnectionsReducer from "./provider-connections/providerConnectionsSlice";
import { cloudChiprApi } from "../services/cloudchipr.api";
import { cloudChiprApiAuthLess } from "../services/cloudchipr-authless.api";
import { cloudChiprApiAccountService } from "../services/cloudchipr-account-service.api";

export const store = configureStore({
  reducer: {
    // keep alphabetical sorting

    accountSetup: accountSetupReducer,
    accounts: accountsReducer,
    alerts: alertsReducer,
    askAi: askAiSlice,
    auth: authReducer,
    automations: automationsSlice,
    budgets: budgetsSlice,
    cleanV2: cleanV2Reducer,
    commitments: commitmentsSlice,
    common: commonReducer,
    dashboards: dashboardsSlice,
    dimensions: dimensionsSlice,
    executionLog: executionLogReducer,
    explainWithAi: explainWithAiSlice,
    filters: filtersReducer,
    integrations: integrationsReducer,
    kubernetesLiveUsageMgmtSlice: kubernetesLiveUsageMgmtSlice,
    liveUsageMgmt: liveUsageMgmtSlice,
    navigation: navigationSlice,
    providerConnections: providerConnectionsReducer,
    recommendations: recommendationsSlice,
    reports: reportsReducer,
    resourceDetails: resourceDetailsSlice,
    resourceExplorer: resourceExplorerSlice,
    resourcesGrid: resourcesGridSlice,
    roles: rolesSlice,
    savingsOpportunities: savingsOpportunitiesSlice,
    schedules: schedulesReducer,
    taskManagement: taskMgmtReducer,
    user: rolesSlice,

    // APIs
    [cloudChiprApi.reducerPath]: cloudChiprApi.reducer,
    [cloudChiprApiAuthLess.reducerPath]: cloudChiprApiAuthLess.reducer,
    [cloudChiprApiAccountService.reducerPath]:
      cloudChiprApiAccountService.reducer,
  },
  middleware: (getDefaultMiddleware) => {
    return getDefaultMiddleware()
      .concat(cloudChiprApi.middleware)
      .concat(cloudChiprApiAuthLess.middleware)
      .concat(cloudChiprApiAccountService.middleware)
      .concat(rtkQueryErrorLoggerMiddleware)
      .concat(unauthenticatedMiddleware);
  },
  devTools: import.meta.env.NODE_ENV !== "production",
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
